/**
 * QuillConfig.js — Configuração global do Quill para o PMAU.
 *
 * Registra o blot HtmlEmbed, que trata elementos não suportados pelo Quill 1.x
 * (tables, divs estilizados, imagens com style + legenda) como blocos opacos,
 * preservando o HTML gerado pela IA integralmente no banco de dados.
 *
 * Fluxo:
 *   DB (HTML limpo)
 *     → preprocessForQuill → Quill (HTML com wrappers ql-html-embed)
 *     → postprocessFromQuill → DB (HTML limpo)
 */

import { Quill } from "vue2-editor";

// ── Blot ──────────────────────────────────────────────────────────────────────

const BlockEmbed = Quill.import("blots/block/embed");

class HtmlEmbed extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute("contenteditable", "false");
    node.innerHTML = value;
    return node;
  }

  static value(node) {
    return node.innerHTML;
  }
}

HtmlEmbed.blotName = "html-embed";
HtmlEmbed.tagName = "div";
HtmlEmbed.className = "ql-html-embed";

Quill.register(HtmlEmbed);

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMBED_CLASS = "ql-html-embed";

/**
 * Envolve elementos não suportados pelo Quill em <div class="ql-html-embed">
 * antes de inserir o HTML no editor. O blot HtmlEmbed reconhece esses wrappers
 * e os preserva como blocos não-editáveis.
 *
 * Elementos tratados:
 *   - <table> com todos seus filhos (caption, thead, tbody, tr, th, td)
 *   - <div style="..."> (placeholders de mapa e clima)
 *   - <figure> (figuras SVG e outros elementos de figura gerados pela IA)
 *   - <img style="..."> + <p style="..."> seguinte (imagem + legenda)
 */
export function preprocessForQuill(html) {
  if (!html) return html;

  const doc = new DOMParser().parseFromString(
    "<div id='root'>" + html + "</div>",
    "text/html"
  );
  const root = doc.getElementById("root");

  // Wrap <table>
  root.querySelectorAll("table").forEach((el) => {
    if (el.closest("." + EMBED_CLASS)) return;
    const wrapper = doc.createElement("div");
    wrapper.className = EMBED_CLASS;
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
  });

  // Wrap <figure> (figuras SVG geradas pela IA)
  root.querySelectorAll("figure").forEach((el) => {
    if (el.closest("." + EMBED_CLASS)) return;
    const wrapper = doc.createElement("div");
    wrapper.className = EMBED_CLASS;
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
  });

  // Wrap <div style="..."> (não é o próprio wrapper do blot)
  root.querySelectorAll("div[style]").forEach((el) => {
    if (el.closest("." + EMBED_CLASS)) return;
    const wrapper = doc.createElement("div");
    wrapper.className = EMBED_CLASS;
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
  });

  // Wrap <img style="..."> e absorve o <p style="..."> seguinte (legenda)
  root.querySelectorAll("img[style]").forEach((el) => {
    if (el.closest("." + EMBED_CLASS)) return;
    const wrapper = doc.createElement("div");
    wrapper.className = EMBED_CLASS;
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
    const next = wrapper.nextElementSibling;
    if (next && next.tagName === "P" && next.hasAttribute("style")) {
      wrapper.appendChild(next);
    }
  });

  return root.innerHTML;
}

/**
 * Remove os wrappers ql-html-embed do HTML produzido pelo Quill, restaurando
 * o HTML original antes de salvar no banco. Os filhos do wrapper são movidos
 * para o pai, mantendo a ordem e os atributos originais.
 */
export function postprocessFromQuill(html) {
  if (!html) return html;

  const doc = new DOMParser().parseFromString(
    "<div id='root'>" + html + "</div>",
    "text/html"
  );
  const root = doc.getElementById("root");

  root.querySelectorAll("." + EMBED_CLASS).forEach((wrapper) => {
    while (wrapper.firstChild) {
      wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
    }
    wrapper.parentNode.removeChild(wrapper);
  });

  return root.innerHTML;
}
